import React, { useState, useEffect, useRef } from "react";
import { IoPersonAddSharp } from "react-icons/io5";
import { MdDelete } from "react-icons/md";
import AddStaffForm from "../add-page";
import { FaEye, FaEyeSlash } from "react-icons/fa";                                                     

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

const TeachersList = () => {
  const [teachers, setTeachers] = useState([]);
  const [deleteMode, setDeleteMode] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showAssignLogin, setShowAssignLogin] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [showEditPhoto, setShowEditPhoto] = useState(false);
  const [photoTeacher, setPhotoTeacher] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [username, setUsername] = useState("");
const [password, setPassword] = useState("");

  const lastTeacherId = useRef(null);

  // Fetch teachers from backend
  useEffect(() => {
    const fetchTeachers = async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const res = await fetch(`${API_BASE_URL}/api/user/teacher?page=${page}&limit=20`);
        if (!res.ok) throw new Error("Failed to fetch teachers");
        const data = await res.json();
        if (page === 1) {
          setTeachers(data.teachers);
        } else {
          setTeachers((prev) => [...prev, ...data.teachers]);
        }
        setHasMore(data.teachers.length === 20);
      } catch (err) {
        setErrorMsg("Could not load teachers.");
      }
      setLoading(false);
    };
    fetchTeachers();
  }, [page]);

  // Handler for deleting a teacher (calls backend API)
  const handleDelete = async (idx) => {
    const teacher = teachers[idx];
    if (!teacher || !teacher._id) return;

    console.log("Attempting to delete teacher:", teacher); // <-- Add this line

    if (!window.confirm(`Are you sure you want to delete ${teacher.name}?`)) return;

    try {
      const url = `${API_BASE_URL}/api/teacher/${teacher._id}`;
      console.log("DELETE request URL:", url); // <-- Add this line
      const res = await fetch(url, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete teacher");
      setTeachers((prev) => prev.filter((_, i) => i !== idx));
    } catch (err) {
      alert("Could not delete teacher. Please try again.");
    }
  };

  // Handler for adding a teacher (show add page)
  const handleAddTeacher = () => {
    setShowAdd(true);
  };

  // Handler to add teacher from AddTeacherPage
  const handleTeacherAdded = (teacher) => {
    setTeachers((prev) => [...prev, teacher]);
    setShowAdd(false);
  };

  // Handler to close add page
  const handleCloseAdd = () => setShowAdd(false);

  const handleAssignLogin = async (teacher) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/teacher/${teacher._id}`);
      if (!res.ok) throw new Error("Failed to fetch teacher details");
      const data = await res.json();
      setSelectedTeacher(data.teacher); // data.teacher should include token
      setShowAssignLogin(true);
    } catch (err) {
      alert("Could not load teacher details. Please try again.");
    }
  };

  const handleCloseAssignLogin = () => {
    setShowAssignLogin(false);
    setSelectedTeacher(null);
  };

  const getTeacherPhotoUrl = (teacher) => {
    if (teacher._id) {
      // Add cache-busting param if photoUpdated exists
      const bust = teacher.photoUpdated ? `?t=${teacher.photoUpdated}` : '';
      return `${API_BASE_URL}/api/teacher/${teacher._id}/photo${bust}`;
    }
    return null;
  };

  const getTeacherUploadUrl = (teacher) => {
    if (teacher.photoFilename) {
      return `${API_BASE_URL}/uploads/teachers/${teacher.photoFilename}`;
    }
    return null;
  };

  const handleEditPhoto = (teacher) => {
    setPhotoTeacher(teacher);
    setShowEditPhoto(true);
  };

  const handleCloseEditPhoto = () => {
    setShowEditPhoto(false);
    setPhotoTeacher(null);
  };

  useEffect(() => {
    if (showAssignLogin && selectedTeacher) {
      if (lastTeacherId.current !== selectedTeacher._id) {
        setUsername(selectedTeacher.name || "");
        setPassword(selectedTeacher.token || "");
        if (Array.isArray(selectedTeacher.subject)) {
          setSubjects(selectedTeacher.subject.length ? selectedTeacher.subject : [""]);
        } else if (typeof selectedTeacher.subject === "string") {
          setSubjects(
            selectedTeacher.subject
              ? selectedTeacher.subject.split(',').map(s => s.trim())
              : [""]
          );
        } else {
          setSubjects([""]);
        }
        if (Array.isArray(selectedTeacher.className)) {
          setClasses(selectedTeacher.className.length ? selectedTeacher.className : [""]);
        } else if (typeof selectedTeacher.className === "string") {
          setClasses(
            selectedTeacher.className
              ? selectedTeacher.className.split(',').map(c => c.trim())
              : [""]
          );
        } else {
          setClasses([""]);
        }
        lastTeacherId.current = selectedTeacher._id;
      }
    }
    // Optionally, clear lastTeacherId on modal close
    if (!showAssignLogin) {
      lastTeacherId.current = null;
    }
  }, [showAssignLogin, selectedTeacher]);

  const isChanged =
    username !== (selectedTeacher?.name || "") ||
    password !== (selectedTeacher?.token || "") ||
    JSON.stringify(classes.filter(c => c.trim())) !== JSON.stringify(
      Array.isArray(selectedTeacher?.className)
        ? selectedTeacher.className.filter(c => c.trim())
        : typeof selectedTeacher?.className === "string"
          ? selectedTeacher.className.split(',').map(c => c.trim()).filter(c => c)
          : []
    ) ||
    JSON.stringify(subjects.filter(s => s.trim())) !== JSON.stringify(
      Array.isArray(selectedTeacher?.subject)
        ? selectedTeacher.subject.filter(s => s.trim())
        : typeof selectedTeacher?.subject === "string"
          ? selectedTeacher.subject.split(',').map(s => s.trim()).filter(s => s)
          : []
    );

  return (
    <>
      {showAdd ? (
        <AddStaffForm
          onAdd={handleTeacherAdded}
          onCancel={handleCloseAdd}
        />
      ) : (
        <>
          <div className="flex gap-3 ml-4">
            <button
              className="flex items-center gap-1 px-3 py-2 bg-primary text-white rounded-lg shadow hover:bg-primary/90 transition"
              onClick={handleAddTeacher}
              title="Add Teacher"
            >
              <IoPersonAddSharp size={20} />
              <span className="hidden sm:inline">Add</span>
            </button>
            <button
              className={`flex items-center gap-1 px-3 py-2 rounded-lg shadow transition ${
                deleteMode
                  ? "bg-red-600 text-white"
                  : "bg-white border border-red-300 text-red-600"
              }`}
              onClick={() => setDeleteMode((d) => !d)}
              title="Delete Teacher"
            >
              <MdDelete size={20} />
              <span className="hidden sm:inline">{deleteMode ? "Cancel" : "Delete"}</span>
            </button>
          </div>
          {/* Total number of teachers */}
          <div className="mb-4 text-lg font-semibold text-primary">
            Total Teachers in School: {teachers.length}
          </div>
          {errorMsg && (
            <div className="mb-4 text-red-600 bg-red-50 border border-red-200 rounded p-2 text-center">
              {errorMsg}
            </div>
          )}
          {loading ? (
            <div className="text-center text-muted-foreground py-12">Loading...</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
              {teachers.map((teacher, idx) => (
                <div
                  key={teacher._id || idx}
                  className={`bg-white border border-border rounded-xl shadow flex flex-col items-center p-4 relative`}
                >
                  {deleteMode && (
                    <button
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-700 transition"
                      onClick={() => handleDelete(idx)}
                      title="Delete this teacher"
                    >
                      <MdDelete size={18} />
                    </button>
                  )}
                  <div className="text-center">
                    <div className="font-semibold text-base text-foreground">{teacher.name}</div>
                    {teacher.loginAssigned && (
                      <span className="inline-block mt-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                        Login Assigned
                      </span>
                    )}
                  </div>
                  <button
                    className="mt-2 px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition"
                    onClick={() => handleAssignLogin(teacher)}
                  >
                    {teacher.loginAssigned ? "Edit Login" : "Assign Login"}
                  </button>
                </div>
              ))}
            </div>
          )}
          {deleteMode && teachers.length === 0 && (
            <div className="text-center text-muted-foreground mt-8">No teachers left.</div>
          )}
          {hasMore && (
            <div className="text-center mt-4">
              <button
                className="px-4 py-2 bg-primary text-white rounded"
                onClick={() => setPage((p) => p + 1)}
                disabled={loading}
              >
                Load More
              </button>
            </div>
          )}
        </>
      )}
      {showEditPhoto && photoTeacher && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-xl"
              onClick={handleCloseEditPhoto}
              aria-label="Close"
            >
              &times;
            </button>
            <h3 className="font-bold text-lg mb-4 text-primary text-center">
              Edit Photo for {photoTeacher.name}
            </h3>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const file = e.target.photo.files[0];
                if (!file) return;
                const formData = new FormData();
                formData.append("photo", file);
                try {
                  const res = await fetch(`${API_BASE_URL}/api/teacher/${photoTeacher._id}/photo`, {
                    method: "POST",
                    body: formData,
                  });
                  if (!res.ok) throw new Error("Failed to upload photo");
                  // Optionally, refresh teacher list or update photoTeacher in state
                  handleCloseEditPhoto();
                  setTeachers((prev) =>
                    prev.map((t) =>
                      t._id === photoTeacher._id ? { ...t, photoUpdated: Date.now() } : t
                    )
                  );
                } catch (err) {
                  alert("Could not upload photo. Please try again.");
                }
              }}
>
              <input
                type="file"
                name="photo"
                accept="image/*"
                className="mb-3"
                required
              />
              <button
                type="submit"
                className="w-full bg-primary text-white py-2 rounded font-semibold mt-2"
              >
                Upload Photo
              </button>
            </form>
          </div>
        </div>
      )}
      {showAssignLogin && selectedTeacher && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto relative">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-xl"
              onClick={handleCloseAssignLogin}
              aria-label="Close"
            >
              &times;
            </button>
            <h3 className="font-bold text-lg mb-4 text-primary text-center sticky top-0 bg-white pb-2">
              Assign Login for {selectedTeacher.name}
            </h3>
            {/* Example form fields */}
            <form
            onSubmit={async (e) => {
  e.preventDefault();
  const formData = new FormData(e.target);
  const className = classes.filter(c => c.trim()); // <-- FIXED
  const name = formData.get('username');
  const token = formData.get('password');
  const term = selectedTeacher.term || "first";
  const subject = subjects.filter(s => s.trim());

  const payload = { id: selectedTeacher._id, name, className, subject, token, term };

  try {
    const res = await fetch(`${API_BASE_URL}/api/auth/assign-teacher-login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (data.success && data.user) {
      setTeachers((prev) =>
        prev.map((t) =>
          t._id === data.user._id ? { ...t, ...data.user } : t
        )
      );
      setSelectedTeacher(data.user);
    }
    handleCloseAssignLogin();
  } catch (err) {
    alert("Could not assign login. Please try again.");
  }
}}
>
              <div className="mb-3">
  <label className="block text-sm font-medium mb-1">Class(es)</label>
  {classes.map((cls, idx) => (
    <div key={idx} className="flex items-center mb-1">
      <input
        type="text"
        className="w-full border rounded px-2 py-1"
        placeholder={`Class ${idx + 1}`}
        value={cls}
        onChange={e => {
          const newClasses = [...classes];
          newClasses[idx] = e.target.value;
          setClasses(newClasses);
        }}
        required
      />
      {classes.length > 1 && (
        <button
          type="button"
          className="ml-2 px-2 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
          onClick={() => setClasses(classes.filter((_, i) => i !== idx))}
          title="Remove class"
        >
          &times;
        </button>
      )}
    </div>
  ))}
  <button
    type="button"
    className="mt-1 px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-xs"
    onClick={() => setClasses([...classes, ""])}
  >
    + Add another class
  </button>
</div>
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">Subject(s)</label>
                {subjects.map((subj, idx) => (
                  <div key={idx} className="flex items-center mb-1">
                    <input
                      type="text"
                      className="w-full border rounded px-2 py-1"
                      placeholder={`Subject ${idx + 1}`}
                      value={subj}
                      onChange={e => {
                        const newSubjects = [...subjects];
                        newSubjects[idx] = e.target.value;
                        setSubjects(newSubjects);
                      }}
                      required
                    />
                    {subjects.length > 1 && (
                      <button
                        type="button"
                        className="ml-2 px-2 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
                        onClick={() => setSubjects(subjects.filter((_, i) => i !== idx))}
                        title="Remove subject"
                      >
                        &times;
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="mt-1 px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-xs"
                  onClick={() => setSubjects([...subjects, ""])}
                >
                  + Add another subject
                </button>
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">Username</label>
                <input
                  name="username"
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  required
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                />
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">Password</label>
                <div className="relative">
                  <input
                    name="password"
                    type={showToken ? "text" : "password"}
                    className="w-full border rounded px-2 py-1 pr-10"
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute top-1/2 right-2 transform -translate-y-1/2 text-gray-500 hover:text-primary"
                    onClick={() => setShowToken((v) => !v)}
                    tabIndex={-1}
                    aria-label={showToken ? "Hide token" : "Show token"}
                  >
                    {showToken ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                className="w-full bg-primary text-white py-2 rounded font-semibold mt-2"
              >
                {isChanged
                  ? "Save Changes"
                  : selectedTeacher.loginAssigned
                    ? "Edit Login"
                    : "Assign Login"}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default TeachersList;